import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import moment from "moment";
import PhoneInput from "react-phone-input-2";
import countries from "i18n-iso-countries";
import fr from "i18n-iso-countries/langs/fr.json";
import { FiPlus } from "react-icons/fi";
import { EmptyState, PageHeader, StatusBadge } from "./DashboardUI";
import Loader from "../loader/Loader";

countries.registerLocale(fr);

const PAGE_SIZE = 25;
const EMPTY_FORM = {
  lastname: "",
  firstname: "",
  email: "",
  country: "CM",
  mobile: "",
  role: "",
};

const fullName = (account) =>
  [account.firstname, account.lastname].filter(Boolean).join(" ") || "—";

const toast = (icon, title, text) =>
  Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 4000,
  }).fire({ icon, title, text });

/*
 * Accounts list of one role (clients, operators, sales).
 * `load(token)` fetches the list; `create`, `update` and `remove` are
 * action creators taking (token, ..., callback). Without `roles`, the
 * account type cannot be chosen and no account can be created.
 */
function AccountsPage({
  title,
  lead,
  accounts,
  load,
  create,
  update,
  remove,
  roles,
  emptyText,
}) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.authUser);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const reload = () => Promise.resolve(dispatch(load(user.token)));

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    reload().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  const term = search.trim().toLowerCase();
  const rows = (accounts || []).filter(
    (account) =>
      !term ||
      [
        fullName(account),
        account.email,
        account.mobile,
        countries.getName(account.country, "fr"),
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(term)),
  );

  const handleCallback = ({ success, message }) => {
    setSubmitting(false);
    if (!success) {
      toast("error", "Échec", message);
      return;
    }
    setEditing(null);
    toast("success", "Succès", message);
    reload();
  };

  const openForm = (account) => {
    setEditing(account || "new");
    setForm(
      account
        ? {
            lastname: account.lastname || "",
            firstname: account.firstname || "",
            email: account.email || "",
            country: account.country || "CM",
            mobile: account.mobile || "",
            role: account.role || "",
          }
        : EMPTY_FORM,
    );
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const data = roles ? form : { ...form, role: undefined };

    setSubmitting(true);
    if (editing === "new") {
      dispatch(create(user.token, data, handleCallback));
    } else {
      dispatch(update(user.token, editing._id, data, handleCallback));
    }
  };

  const toggleStatus = (account) => {
    const suspended = account.status === "suspended";

    Swal.fire({
      title: suspended ? "Débloquer le compte ?" : "Bloquer le compte ?",
      text: suspended
        ? `${fullName(account)} pourra à nouveau se connecter.`
        : `${fullName(account)} ne pourra plus se connecter.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#051259",
      cancelButtonColor: "#9aa0ad",
      confirmButtonText: suspended ? "Débloquer" : "Bloquer",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setSubmitting(true);
      dispatch(
        update(
          user.token,
          account._id,
          { status: suspended ? "active" : "suspended" },
          handleCallback,
        ),
      );
    });
  };

  const handleDelete = (account) => {
    Swal.fire({
      title: "Supprimer le compte ?",
      text: `Le compte de ${fullName(account)} sera supprimé. Cette action est irréversible.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#9aa0ad",
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
    }).then((result) => {
      if (!result.isConfirmed) return;
      setSubmitting(true);
      dispatch(remove(user.token, account._id, handleCallback));
    });
  };

  const roleLabel = (value) =>
    roles?.find((role) => role.value === value)?.label;

  return (
    <>
      <Loader isLoading={submitting} />
      <PageHeader
        title={title}
        lead={
          loading
            ? lead
            : `${(accounts || []).length} compte${
                (accounts || []).length > 1 ? "s" : ""
              }. ${lead}`
        }
        actions={
          create && (
            <button
              type="button"
              className="dash-button"
              onClick={() => openForm(null)}
            >
              <FiPlus aria-hidden="true" /> Ajouter
            </button>
          )
        }
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher un nom, un e-mail, un téléphone…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setVisible(PAGE_SIZE);
            }}
            aria-label="Rechercher"
          />
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des comptes…</p>
        ) : rows.length === 0 ? (
          <EmptyState title="Aucun compte">
            {accounts?.length
              ? "Aucun compte ne correspond à votre recherche."
              : emptyText}
          </EmptyState>
        ) : (
          <>
            <div className="dash-table-wrap">
              <table className="dash-table is-responsive">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>E-mail</th>
                    <th>Téléphone</th>
                    <th>Pays</th>
                    <th>Inscrit le</th>
                    <th>Statut</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.slice(0, visible).map((account) => (
                    <tr key={account._id}>
                      <td>
                        <span className="dash-cell-title">
                          {fullName(account)}
                        </span>
                        {roleLabel(account.role) && (
                          <span className="dash-cell-sub">
                            {roleLabel(account.role)}
                          </span>
                        )}
                      </td>
                      <td data-label="E-mail">{account.email || "—"}</td>
                      <td data-label="Téléphone">{account.mobile || "—"}</td>
                      <td data-label="Pays">
                        {countries.getName(account.country, "fr") ||
                          account.country ||
                          "—"}
                      </td>
                      <td data-label="Inscrit le">
                        {account.createdAt
                          ? moment(account.createdAt).format("DD/MM/YYYY")
                          : "—"}
                      </td>
                      <td data-label="Statut">
                        {account.status === "suspended" ? (
                          <StatusBadge status="failed" label="Bloqué" />
                        ) : (
                          <StatusBadge status="fulfilled" label="Actif" />
                        )}
                      </td>
                      <td>
                        <div className="dash-row-actions">
                          <button
                            type="button"
                            className="dash-link-button"
                            onClick={() => openForm(account)}
                          >
                            Modifier
                          </button>
                          <button
                            type="button"
                            className="dash-link-button"
                            onClick={() => toggleStatus(account)}
                          >
                            {account.status === "suspended"
                              ? "Débloquer"
                              : "Bloquer"}
                          </button>
                          <button
                            type="button"
                            className="dash-link-button is-danger"
                            onClick={() => handleDelete(account)}
                          >
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {rows.length > visible && (
              <div className="dash-more">
                <button
                  type="button"
                  className="dash-button is-secondary"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                >
                  Afficher plus
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Modal show={Boolean(editing)} onHide={() => setEditing(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title as="h2" className="dash-card-title mb-0">
            {editing === "new" ? "Nouveau compte" : "Modifier le compte"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form className="dash-form" onSubmit={handleSubmit}>
            <label htmlFor="account-lastname">Nom</label>
            <input
              id="account-lastname"
              value={form.lastname}
              onChange={(e) => setForm({ ...form, lastname: e.target.value })}
              required
            />

            <label htmlFor="account-firstname">Prénom</label>
            <input
              id="account-firstname"
              value={form.firstname}
              onChange={(e) => setForm({ ...form, firstname: e.target.value })}
              required
            />

            <label htmlFor="account-email">E-mail</label>
            <input
              id="account-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
            />

            <label htmlFor="account-mobile">Téléphone</label>
            <PhoneInput
              inputProps={{ id: "account-mobile" }}
              country={(form.country || "cm").toLowerCase()}
              value={form.mobile}
              onChange={(phone) =>
                setForm({
                  ...form,
                  mobile: phone.startsWith("+") ? phone : `+${phone}`,
                })
              }
              inputStyle={{ width: "100%", height: 40 }}
            />

            {roles && (
              <>
                <label htmlFor="account-role">Type de compte</label>
                <select
                  id="account-role"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  required
                >
                  <option value="">Choisissez le type de compte</option>
                  {roles.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </>
            )}

            <div className="dash-form-actions">
              <button
                type="button"
                className="dash-button is-secondary"
                onClick={() => setEditing(null)}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="dash-button"
                disabled={submitting}
              >
                {editing === "new" ? "Créer le compte" : "Enregistrer"}
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default AccountsPage;
